import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import RatingComponentPageWrapper from 'components/rating/RatingComponentPageWrapper';
import RightPanel from 'app/home/RightPanel';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { homePage } from 'api/home-page';
import { useAppSelector } from 'store/hooks';
import { UserAuth } from 'store/auth';
import { cloneDeep, debounce } from 'lodash';
import {
    decompressTiles,
    buildFolderMap,
    TileInfo,
    HomePageCachedData,
    HomePageData,
    TopicsPopupProps,
    HomePageState,
    extendStateFromHash,
    getHashFromState,
    AccessDeniedTileInfo,
    getCachedDashboardData,
    setCachedDashboardData,
    ContextMenuType,
    getGroupByOptions,
    getSegmentOptions,
} from './index.functions';
import { HomeContext } from 'app/home/context';
import styles from './index.styles';
import AddToFavoritePopup from 'components/common/favorite/AddToFavoritePopup';
import AccessDeniedPopup from 'components/common/popup/AccessDeniedPopup';
import GlossaryTermPopup from 'components/glossary-term-popup';
import Preview from './tile/preview/Preview';
import { useHash } from 'hooks/useHash';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Stack } from '@mui/material';
import { debounceFunc } from 'tools/tools';
import ContextMenu from 'app/home/tile/ContextMenu';
import useBundleTranslation from 'i18n';
import { useDispatch } from 'react-redux';
import { setPageTitle } from 'store/pageTitleSlice';
import { useSearchParams, useParams } from 'react-router-dom';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { TileSection } from 'app/home/tile/TilePanel.functions';
import { useCatalogIdByAlias } from 'hooks/useCatalog';

let isDataLoaded = false;

export interface Tile4FavoriteData {
    elementId: number;
    segmentValueId: number;
    elementName: string;
}

export const homePageStateDefault = {
    hierarchyPanelOpen: true,
    //hierarchyPanelWidth: 280,
    expandedFolders: {},
    collapsedSections: {},
    filterState: {
        view: 'tile',
        groupBy: '',
        folderSelected: '',
        searchFilter: '',
        tagFilter: [],
        segmentFilter: '',
        keywordFilter: '',
        lastFilterApplied: '',
    },
};

function useHomePageHash(tilePreview: TileInfo | null, homePageState?: HomePageState) {
    const [hash, setHash] = useHash();
    const [autoShowPreview, setAutoShowPreview] = useState<[number, number] | false>(false);
    const [hashIndex, setHashIndex] = useState(0);
    useEffect(() => {
        const onHashChange = () => {
            setHashIndex((prev) => prev + 1);
        };
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    useEffect(() => {
        const match = hash.match(/\/preview\/element\/(\d+)(?:\/segment\/(\d+))?/);
        if (match && match.length) {
            setAutoShowPreview([Number(match[1]), Number(match[2] ?? 0)]);
        }
    }, [hashIndex]);

    const getPreviewPart = (e: number, s: number) => {
        let result = `/preview/element/${e}`;
        if (s) {
            result += `/segment/${s}`;
        }
        return result;
    };

    useEffect(() => {
        if (!homePageState) {
            return;
        }
        let newHash = getHashFromState(homePageState);
        if (tilePreview?.element_id) {
            newHash += getPreviewPart(tilePreview.element_id, tilePreview?.segment_value_id ?? 0);
        } else if (autoShowPreview) {
            newHash += getPreviewPart(autoShowPreview[0], autoShowPreview[1]);
        }
        if (window.location.hash != newHash) {
            setHash(newHash);
        }
    }, [homePageState, tilePreview]);
    return { hash, setHash, autoShowPreview, setAutoShowPreview };
}

function Home() {
    const userAuth: UserAuth = useAppSelector((state) => state.auth);
    const projectVersion = userAuth.configuration.constants.PROJECT_VERSION;
    const { t } = useBundleTranslation(['app/home']);
    const [urlSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const urlParams = useParams();
    const catalog = urlParams?.catalog ?? '';
    const queryKeyPrefix = catalog > '' ? catalog + 'Catalog' : 'homePage';
    const currentCatalogId = useCatalogIdByAlias(catalog);

    const {
        data: homePageSettingsData,
        refetch: refetchHomeSettings,
        isLoading: isLoadingHomeSettings,
    } = useQuery<any | void, Error>({
        queryKey: [queryKeyPrefix + 'Settings'],
        queryFn: () => homePage.getHomeSettings({ catalog }),
    });

    const {
        data: loadedData,
        isLoading,
        refetch,
    } = useQuery<any, Error>({
        queryKey: [queryKeyPrefix + 'Data'],
        queryFn: () => {
            return homePage.getData({ catalog }).then((data) => {
                isDataLoaded = false;
                return data;
            });
        },
        refetchOnMount: true,
    });

    useEffect(() => {
        if (undefined === loadedData || null == loadedData) return;

        let str = JSON.stringify({
            data: loadedData.data,
            hierarchyFolders: loadedData.hierarchyFolders,
            folderItems: loadedData.folderItems,
            tags: loadedData.topics,
            tileIcons: loadedData.tileIcons,
            contentTypes: loadedData.contentTypes,
            customFieldsDisplayOnTileInfo: loadedData.customFieldsDisplayOnTileInfo,
        });
        setCachedDashboardData(str, str.length > 524288 ? 'Y' : 'N', projectVersion, catalog);
    }, [loadedData]);

    const [tile4Favorite, setTile4Favorite] = useState<Tile4FavoriteData | null>(null);

    const cachedData: HomePageCachedData | null = getCachedDashboardData(projectVersion, catalog);

    const [data, setData] = useState<HomePageData>(
        cachedData && cachedData.data
            ? {
                  data: cachedData.data,
                  hierarchyFolders: cachedData.hierarchyFolders,
                  folderItems: cachedData.folderItems,
                  tags: cachedData.tags,
                  tileIcons: cachedData.tileIcons,
                  contentTypes: cachedData.contentTypes,
                  customFieldsDisplayOnTileInfo: cachedData.customFieldsDisplayOnTileInfo,
              }
            : {
                  data: { vocabulary: [], rows: [] },
                  hierarchyFolders: [],
                  folderItems: [],
                  tags: [],
                  tileIcons: {},
                  contentTypes: [],
                  customFieldsDisplayOnTileInfo: {},
              },
    );
    const [homePageState, setHomePageState] = useState<HomePageState>();

    const [isPending, startTransition] = useTransition();
    const [showTermsPopup, setShowTermsPopup] = useState(false);
    const [tilePreview, setTilePreview] = useState<TileInfo | null>(null);
    const { hash, autoShowPreview, setAutoShowPreview } = useHomePageHash(tilePreview, homePageState);
    const [accessDeniedPopupTile, setAccessDeniedPopupTile] = useState<AccessDeniedTileInfo | null>(null);
    const [visibleTiles, setVisibleTiles] = useState<TileInfo[]>([]);
    const tilesSectionsRef = useRef<TileSection[]>();
    const [topicId, setTopicId] = useState<number>(0);
    const [elementId, setElementId] = useState<number>(0);

    let trashBucketRef = React.useRef<HTMLDivElement>(null);

    const topicPopupProps: TopicsPopupProps = useMemo(() => {
        return {
            setShowTermsPopup,
            setTopicId,
            setElementId,
        };
    }, []);

    const updateFavoriteStateRow = (elementId: number, segmentValueId: number, ids: Array<string>) => {
        if (!loadedData?.data?.vocabulary) {
            return;
        }
        const eIndex = loadedData.data.vocabulary.findIndex((v: string) => v == 'element_id');
        const sIndex = loadedData.data.vocabulary.findIndex((v: string) => v == 'segment_value_id');
        const stIndex = loadedData.data.vocabulary.findIndex((v: string) => v == 'section_type');
        const fIndex = loadedData.data.vocabulary.findIndex((v: string) => v == 'favorite_id');
        const cIndex = loadedData.data.vocabulary.findIndex((v: string) => v == 'category_id');
        const rowIndex = loadedData.data.rows.findIndex(
            (r: any) => r[eIndex] == elementId && r[sIndex] == segmentValueId,
        );

        const fields: { [key: string]: any } = {
            in_favorites: ids.length == 0 ? null : ids.join(','),
            is_in_favorites: ids.length == 0 ? 0 : 1,
        };

        if (rowIndex != -1) {
            setData((prevState) => {
                const state = structuredClone(prevState);

                for (const key in fields) {
                    const fIndex = loadedData.data.vocabulary.findIndex((v: string) => v == key);
                    if (fIndex != -1) {
                        state.data.rows[rowIndex][fIndex] = fields[key];
                    }
                }

                state.data.rows
                    .filter(
                        (r: any) => r[eIndex] == elementId && r[sIndex] == segmentValueId && r[stIndex] == 'Favorite',
                    )
                    .forEach((r: any) => {
                        for (const key in fields) {
                            const fIndex = loadedData.data.vocabulary.findIndex((v: string) => v == key);
                            if (fIndex != -1) {
                                r[fIndex] = fields[key];
                            }
                        }

                        if (fIndex != -1 && !ids.includes(String(r[fIndex]))) {
                            r[fIndex] = 0;
                        }
                    });

                // Add to selected favorites
                ids.forEach((favoriteId) => {
                    if (
                        state.data.rows.findIndex(
                            (r) =>
                                r[eIndex] == String(elementId) &&
                                r[sIndex] == String(segmentValueId) &&
                                r[fIndex] == favoriteId,
                        ) != -1
                    ) {
                        return; // Skip if favorite already exists
                    }
                    const newRow = structuredClone(state.data.rows[rowIndex]);
                    newRow[stIndex] = 'Favorite';
                    newRow[fIndex] = favoriteId;
                    newRow[cIndex] = '0';
                    state.data.rows.push(newRow);
                });
                // Remove unselected favorites
                state.data.rows = state.data.rows.filter((r) => !(r[stIndex] == 'Favorite' && r[fIndex] == '0'));
                return state;
            });
        }
    };

    const afterSaveFavorite = (elementId: number, segmentValueId: number, ids: Array<string>) => {
        setTile4Favorite(null);
        updateFavoriteStateRow(elementId, segmentValueId, ids);
        // refetch();
        // isDataLoaded = false;
    };

    const handleCloseFavorite = useCallback(() => {
        setTile4Favorite(null);
    }, []);

    const refetchData = useCallback(() => {
        if (!homePageState) return;
        homePage.saveState(homePageState, catalog).then(() => {
            refetch();
        });
        isDataLoaded = false;
    }, [refetch, homePageState, catalog]);

    useEffect(() => {
        if (!isLoading && !isDataLoaded) {
            isDataLoaded = true;
            startTransition(() => {
                setData({
                    data: loadedData.data,
                    hierarchyFolders: loadedData.hierarchyFolders,
                    folderItems: loadedData.folderItems,
                    tags: loadedData.topics,
                    tileIcons: loadedData.tileIcons,
                    contentTypes: loadedData.contentTypes,
                    customFieldsDisplayOnTileInfo: loadedData.customFieldsDisplayOnTileInfo,
                });
            });
        }
    }, [loadedData]);
    useEffect(() => {
        if (!isLoadingHomeSettings && homePageSettingsData) {
            let newStateData = {
                ...homePageStateDefault,
                ...(homePageSettingsData?.state ?? {}),
                filterState: {
                    ...homePageStateDefault.filterState,
                    ...(homePageSettingsData?.state?.filterState ?? {}),
                },
            };

            if (hash && hash.length > 0) {
                newStateData = extendStateFromHash(newStateData, hash);
            }

            setHomePageState(newStateData);
        }
    }, [isLoadingHomeSettings, homePageSettingsData]);

    const debouncedSaveStateHandler = useMemo(
        () =>
            debounce((state: HomePageState) => {
                if (!isDataLoaded) return;

                let newState: any = cloneDeep(state);
                if ('Y' == homePageSettingsData?.settings?.parameters?.HOMEPAGE_CLEAR_ALL_FILTERS) {
                    newState.filterState = {
                        ...homePageStateDefault.filterState,
                        view: newState?.filterState?.view ?? homePageStateDefault.filterState.view,
                    };
                }
                homePage.saveState(newState, catalog);
            }, 200),
        [homePageSettingsData?.settings?.parameters?.HOMEPAGE_CLEAR_ALL_FILTERS],
    );

    useEffect(() => {
        return () => {
            queryClient.invalidateQueries({ queryKey: [queryKeyPrefix + 'Data', queryKeyPrefix + 'Settings'] });
        };
    }, []);

    useEffect(() => {
        if (homePageState) {
            if (!isDataLoaded) {
                return;
            }
            const debounced = debounceFunc(() => debouncedSaveStateHandler(homePageState), 100);
            debounced();
        }
    }, [homePageState]);

    useEffect(() => {
        if (homePageSettingsData?.settings?.title && homePageSettingsData?.settings?.title > '') {
            dispatch(setPageTitle(homePageSettingsData.settings.title));
        }
    }, [homePageSettingsData]);

    const [favorites, folders, categories, folderMap] = useMemo(
        () => buildFolderMap(cloneDeep(data.hierarchyFolders)),
        [data.hierarchyFolders],
    );
    const tiles: TileInfo[] = useMemo(() => decompressTiles(data), [data]);

    useEffect(() => {
        if (tilesSectionsRef.current == undefined || tiles.length == 0 || autoShowPreview == false) {
            return;
        }

        const tileToShow = tiles.find(
            (tile) => tile.element_id == autoShowPreview[0] && tile.segment_value_id == autoShowPreview[1],
        );
        if (tileToShow) {
            setTilePreview(tileToShow);
            setVisibleTiles(tilesSectionsRef.current.flatMap((s) => s.tiles));
        }

        setAutoShowPreview(false);
    }, [tiles, autoShowPreview, tilesSectionsRef.current]);

    const topicIds: number[] = useMemo(() => {
        const result: number[] = [];

        if (tiles && elementId > 0) {
            tiles.forEach((tile) => {
                if (tile.topics && tile.element_id === elementId) result.push(...tile.topics);
            });
        }

        return result;
    }, [tiles, elementId]);

    useEffect(() => {
        if (!showTermsPopup) {
            setElementId(0);
        }
    }, [showTermsPopup]);

    const onNextPreviewClick = useCallback(
        (next: boolean = true): void => {
            const currentTileIndex = visibleTiles.findIndex(
                (t) =>
                    t.element_id === tilePreview?.element_id &&
                    t.user_dashboard_element_instance_id === tilePreview?.user_dashboard_element_instance_id,
            );

            let nextTileIndex = currentTileIndex + 1;
            if (nextTileIndex >= visibleTiles.length) {
                nextTileIndex = 0;
            }

            let prevTileIndex = currentTileIndex - 1;
            if (prevTileIndex < 0) {
                prevTileIndex = visibleTiles.length - 1;
            }

            const newTile = next ? visibleTiles[nextTileIndex] : visibleTiles[prevTileIndex];
            setTilePreview(newTile);
        },
        [visibleTiles, tilePreview],
    );

    const [contextMenu, setContextMenu] = React.useState<ContextMenuType>(null);

    const [contextMenuTile, setContextMenuTile] = React.useState<TileInfo | null>(null);
    const handleContextMenu = (event: React.MouseEvent, tile: TileInfo | null) => {
        setContextMenu(null);
        event.preventDefault();
        setContextMenuTile(tile);
        setContextMenu(
            {
                mouseX: event.clientX + 2,
                mouseY: event.clientY - 6,
            },
            // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
            // Other native context menus might behave different.
            // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
        );
    };

    // const memorizedRightPanel = useMemo(
    //     () => (
    //         <RightPanel
    //             tiles={tiles}
    //             setTile4Favorite={setTile4Favorite}
    //             customFieldsDisplayOnTileInfo={data.customFieldsDisplayOnTileInfo}
    //         />
    //     ),
    //     [tiles, data.customFieldsDisplayOnTileInfo, topicPopupProps, homePageState],
    // );

    const groupByOptions = getGroupByOptions(homePageSettingsData?.settings, t);
    let activeSegmentOption =
        homePageState?.filterState?.segmentFilter && homePageState?.filterState?.segmentFilter > ''
            ? homePageState.filterState.segmentFilter
            : '';
    const segmentOptions = getSegmentOptions(data, homePageSettingsData?.settings, tiles, activeSegmentOption, t);
    const tagOptions = data?.tags.map((t) => ({ value: String(t.topic_id), label: t.full_name }));

    const isEmbedded: boolean = window.self !== window.top;
    const navigationBack = isEmbedded || urlSearchParams.get('navigation_back') != 'N';
    const newTab = isEmbedded || urlSearchParams.get('new_tab') == 'N';

    if (!homePageState) {
        return <LoadingPlaceholder variant={'secondary'} />;
    }

    if (null == data || null == data.hierarchyFolders || 0 == data.hierarchyFolders.length) return <></>;

    return (
        <>
            <HomeContext.Provider
                value={{
                    tiles,
                    favorites,
                    folders,
                    categories,
                    folderMap,
                    homePageState,
                    setHomePageState,
                    data,
                    setData,
                    refetchData,
                    trashBucketRef,
                    tilePreview,
                    setTilePreview,
                    setAccessDeniedPopupTile,
                    setVisibleTiles,
                    tilesSectionsRef,
                    handleContextMenu,
                    setTile4Favorite,
                    groupByOptions,
                    segmentOptions,
                    tagOptions,
                    isEmbedded: isEmbedded,
                    navigationBack: navigationBack,
                    newTab: newTab,
                    topicPopupProps,
                    logoImageUrl: homePageSettingsData?.logoImageUrl,
                    homePageSettings: homePageSettingsData?.settings,
                    catalog,
                    currentCatalogId,
                }}
            >
                <Stack sx={styles.dashboardWrapper} className={'dashboard dashboard-wrapper'}>
                    <DndProvider backend={HTML5Backend}>
                        <Suspense>
                            <RightPanel
                                tiles={tiles}
                                setTile4Favorite={setTile4Favorite}
                                customFieldsDisplayOnTileInfo={data.customFieldsDisplayOnTileInfo}
                            />
                        </Suspense>
                        <Suspense>
                            {tile4Favorite && (
                                <AddToFavoritePopup
                                    elementId={tile4Favorite.elementId}
                                    segmentValueId={tile4Favorite.segmentValueId}
                                    isOpen={true}
                                    onApply={afterSaveFavorite}
                                    onClose={handleCloseFavorite}
                                    scope={'dashboard'}
                                    elementName={tile4Favorite.elementName}
                                />
                            )}
                        </Suspense>
                    </DndProvider>
                </Stack>
                <RatingComponentPageWrapper itemType={'page'} itemId={'0'} />
                <Suspense>
                    {showTermsPopup && (
                        <GlossaryTermPopup
                            setShowPopup={setShowTermsPopup}
                            showPopup={showTermsPopup}
                            topicIds={topicIds}
                            topicId={topicId}
                        />
                    )}
                </Suspense>
                <Suspense>
                    {tilePreview && (
                        <Preview
                            refetchData={() => refetch()}
                            tile={tilePreview}
                            onNextPreviewClick={onNextPreviewClick}
                            hideNextPrevButtons={!visibleTiles || visibleTiles.length < 2}
                        />
                    )}
                </Suspense>
                <Suspense>
                    {accessDeniedPopupTile && (
                        <AccessDeniedPopup
                            accessDeniedTile={accessDeniedPopupTile}
                            setAccessDeniedPopupTile={setAccessDeniedPopupTile}
                        />
                    )}
                </Suspense>
                <ContextMenu
                    contextMenu={contextMenu}
                    tile={contextMenuTile}
                    setContextMenuTile={setContextMenuTile}
                    setContextMenu={setContextMenu}
                />
            </HomeContext.Provider>
        </>
    );
}

export default Home;
